import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AxiosInstance from '../configs/axiosConfig';
import { v4 as uuidv4 } from 'uuid';
import { Modal, Box, TextField, Button, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useSnackbar } from '../contexts/SnackbarContext';
import PatientSuccessModal from './PatientSuccessModal';

const PRIMARY_COLOR = "#003595";
interface AddPatientModalProps {
    open: boolean;
    onClose: () => void;
    onPatientAdded?: () => void;  // Add this line
}

const AddPatientModal: React.FC<AddPatientModalProps> = ({ open, onClose, onPatientAdded }) => {
    const { showSuccess, showError } = useSnackbar();
    const [formData, setFormData] = useState({
        profile: {
            firstName: '',
            lastName: '',
            email: '',
            login: '',
        },
        evaluationDuration: ''
    });
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [newPatientId, setNewPatientId] = useState<string>('');
    
    // Email validation states
    const [emailValidating, setEmailValidating] = useState(false);
    const [emailError, setEmailError] = useState<string>('');
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    
    // Submit loading state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Debounce email validation
    const validateEmail = useCallback(
        async (email: string) => {
            if (!email.trim()) {
                setEmailError('');
                setIsEmailValid(false);
                return;
            }

            // Basic email format validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                setEmailError('Please enter a valid email format');
                setIsEmailValid(false);
                return;
            }

            setEmailValidating(true);
            setEmailError('');

            try {
                const response = await axios.get(`${process.env.REACT_APP_VALIDATE_EMAIL_URL}/validate?email=${email}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'client_id': process.env.REACT_APP_MULESOFT_VALIDATE_EMAIL_CLIENT_ID || '',
                        'client_secret': process.env.REACT_APP_MULESOFT_VALIDATE_EMAIL_CLIENT_SECRET || ''
                    }
                });
                console.log('Email validation response:', response.data);
                if (response.data && (response.data.results.status === "valid" || response.data.results.status === "risky")) {
                    setIsEmailValid(true);
                    setEmailError('');
                } else {
                    setIsEmailValid(false);
                    setEmailError(response.data?.message || 'Email validation failed');
                }
            } catch (error: any) {
                setIsEmailValid(false);
                if (error.response?.status === 400) {
                    setEmailError(error.response.data?.message || 'Invalid email address');
                } else if (error.response?.status === 409) {
                    setEmailError('Email address is already in use');
                } else {
                    setEmailError('Email validation failed. Please try again.');
                }
                console.error('Email validation error:', error);
            } finally {
                setEmailValidating(false);
            }
        },
        []
    );

    // Debounce timer for email validation
    useEffect(() => {
        const timer = setTimeout(() => {
            if (formData.profile.email) {
                validateEmail(formData.profile.email);
            }
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [formData.profile.email, validateEmail]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        // Clear email validation states when email changes
        if (name === 'email') {
            setEmailError('');
            setIsEmailValid(false);
        }
        
        setFormData(prev => ({
            ...prev,
            profile: {
                ...prev.profile,
                [name]: value,
                // Set login to email value when email changes
                ...(name === 'email' && { login: value })
            }
        }));
    };

    const handleDropdownChange = (e: any) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        // Validate form before submission
        if (!formData.profile.firstName.trim() || !formData.profile.lastName.trim() || !formData.profile.email.trim() || !formData.evaluationDuration) {
            showError('Please fill in all required fields');
            return;
        }

        if (!isEmailValid) {
            showError('Please enter a valid email address');
            return;
        }

        console.log('Patient to add:', formData);
        
        setIsSubmitting(true);

        try {
            const response = await AxiosInstance.post(`/patients?call=webapp`, formData);
            console.log("Response", response.data);
            
            // Assuming response.data.id is the patient ID
            setNewPatientId(response.data.id || '');
            setSuccessModalOpen(true); // Show success modal

            // Reset form data
            setFormData({
                profile: {
                    firstName: '',
                    lastName: '',
                    email: '',
                    login: '',
                },
                evaluationDuration: ''
            });
            
            // Reset email validation states
            setEmailError('');
            setIsEmailValid(false);
            setEmailValidating(false);

            onClose();
            onPatientAdded?.(); // Add this line
        } catch (error) {
            // Error is already handled by global interceptor
            // Just log it for debugging
            console.debug("error>>>", error);
            showError('Failed to create patient. Please try again.');
            // Optionally, you can add specific error handling here
            // or use the skipGlobalError config if needed
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form data when closing
        setFormData({
            profile: {
                firstName: '',
                lastName: '',
                email: '',
                login: '',
            },
            evaluationDuration: ''
        });
        
        // Reset email validation states
        setEmailError('');
        setIsEmailValid(false);
        setEmailValidating(false);
        setIsSubmitting(false);
        
        onClose();
    };

    return (
        <>
            <Modal open={open} onClose={handleClose}>
                <Box sx={{
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400, 
                    bgcolor: 'background.paper',
                    boxShadow: 24, 
                    p: 4, 
                    borderRadius: 2
                }}>
                    <Typography variant="h6" gutterBottom>Add Patient</Typography>
                    <TextField 
                        fullWidth 
                        label="First Name" 
                        name="firstName" 
                        value={formData.profile.firstName}
                        margin="normal" 
                        onChange={handleChange}
                        required
                    />
                    <TextField 
                        fullWidth 
                        label="Last Name" 
                        name="lastName" 
                        value={formData.profile.lastName}
                        margin="normal" 
                        onChange={handleChange}
                        required
                    />
                    <TextField 
                        fullWidth 
                        label="Email" 
                        name="email" 
                        type="email"
                        value={formData.profile.email}
                        margin="normal" 
                        onChange={handleChange}
                        error={!!emailError}
                        helperText={emailError}
                        InputProps={{
                            endAdornment: emailValidating ? (
                                <CircularProgress size={20} />
                            ) : null,
                        }}
                        required
                    />
                    <FormControl fullWidth margin="normal" required>
                        <InputLabel id="evaluation-duration-label">Evaluation Duration</InputLabel>
                        <Select
                            labelId="evaluation-duration-label"
                            id="evaluation-duration"
                            name="evaluationDuration"
                            value={formData.evaluationDuration}
                            label="Evaluation Duration"
                            onChange={handleDropdownChange}
                        >
                            <MenuItem value="1 day">1 day</MenuItem>
                            <MenuItem value="2 days">2 days</MenuItem>
                            <MenuItem value="3 days">3 days</MenuItem>
                            <MenuItem value="4 days">4 days</MenuItem>
                            <MenuItem value="5 days">5 days</MenuItem>
                            <MenuItem value="6 days">6 days</MenuItem>
                            <MenuItem value="7 days">7 days</MenuItem>
                            <MenuItem value="8 days">8 days</MenuItem>
                            <MenuItem value="9 days">9 days</MenuItem>
                            <MenuItem value="10 days">10 days</MenuItem>
                            <MenuItem value="11 days">11 days</MenuItem>
                            <MenuItem value="12 days">12 days</MenuItem>
                            <MenuItem value="13 days">13 days</MenuItem>
                            <MenuItem value="14 days">14 days</MenuItem>
                            <MenuItem value="15 days">15 days</MenuItem>
                            <MenuItem value="16 days">16 days</MenuItem>
                            <MenuItem value="17 days">17 days</MenuItem>
                            <MenuItem value="18 days">18 days</MenuItem>
                            <MenuItem value="19 days">19 days</MenuItem>
                            <MenuItem value="20 days">20 days</MenuItem>
                            <MenuItem value="21 days">21 days</MenuItem>
                        </Select>
                    </FormControl>
                    <Box mt={2} display="flex" justifyContent="space-between">
                        <Button 
                            variant="contained"
                            sx={{
                                bgcolor: PRIMARY_COLOR,
                                "&:hover": {
                                    bgcolor: "#002566",
                                },
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                letterSpacing: 1,
                            }}
                            onClick={handleSubmit}
                            disabled={
                                !formData.profile.firstName.trim() || 
                                !formData.profile.lastName.trim() || 
                                !formData.profile.email.trim() || 
                                !formData.evaluationDuration ||
                                !isEmailValid || 
                                emailValidating ||
                                isSubmitting
                            }
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <PatientSuccessModal
                open={successModalOpen}
                patientId={newPatientId}
                onClose={() => setSuccessModalOpen(false)}
            />
        </>
    );
};

export default AddPatientModal;
